import React, { useState, useMemo } from 'react';
import { FileText, Download, Calendar, User, BarChart3, TrendingUp, Filter, PieChart } from 'lucide-react';
import { formatDate } from '../utils/dataModels';
import ScreenReaderContent from './ScreenReaderContent';

const Reports = ({ goals, students, onNavigate, announce }) => {
  const [selectedStudentId, setSelectedStudentId] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: formatDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), 'YYYY-MM-DD'), // 90 days ago
    end: formatDate(new Date(), 'YYYY-MM-DD')
  });
  const [reportType, setReportType] = useState('summary');

  // Filter goals and progress data based on selected criteria
  const filteredData = useMemo(() => {
    let filteredGoals = goals;
    
    if (selectedStudentId !== 'all') {
      filteredGoals = goals.filter(goal => goal.studentId === selectedStudentId);
    }

    // Filter progress data by date range
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    
    filteredGoals = filteredGoals.map(goal => ({
      ...goal,
      progressData: goal.progressData.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= startDate && entryDate <= endDate;
      })
    }));

    return filteredGoals;
  }, [goals, selectedStudentId, dateRange]);

  // Generate report statistics
  const reportStats = useMemo(() => {
    const totalGoals = filteredData.length;
    const totalStudents = selectedStudentId === 'all' 
      ? new Set(filteredData.map(goal => goal.studentId)).size 
      : 1;
    
    const statusCounts = filteredData.reduce((acc, goal) => {
      acc[goal.status] = (acc[goal.status] || 0) + 1;
      return acc;
    }, {});

    const domainCounts = filteredData.reduce((acc, goal) => {
      acc[goal.domain] = (acc[goal.domain] || 0) + 1;
      return acc;
    }, {});

    const totalProgressEntries = filteredData.reduce((sum, goal) => sum + goal.progressData.length, 0);
    
    const averageProgress = filteredData.reduce((sum, goal) => {
      const latestEntry = goal.progressData[goal.progressData.length - 1];
      return sum + (latestEntry ? latestEntry.value : 0);
    }, 0) / Math.max(totalGoals, 1);

    return {
      totalGoals,
      totalStudents,
      totalProgressEntries,
      averageProgress: Math.round(averageProgress * 10) / 10,
      statusCounts,
      domainCounts
    };
  }, [filteredData, selectedStudentId]);

  const handlePrintReport = () => {
    announce('Preparing report for printing...');
    window.print();
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'text-gray-600',
      'In Progress': 'text-yellow-600',
      'Mastered': 'text-green-600',
      'Discontinued': 'text-red-600'
    };
    return colors[status] || 'text-gray-600';
  };

  const getProgressTrend = (goal) => {
    if (!goal.progressData || goal.progressData.length < 2) return 'no-trend';
    
    const recent = goal.progressData.slice(-2);
    const current = recent[1].value;
    const previous = recent[0].value;
    
    if (current > previous) return 'improving';
    if (current < previous) return 'declining';
    return 'stable';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return '↗';
      case 'declining': return '↘';
      case 'stable': return '→';
      default: return '—';
    }
  };

  const getTrendColor = (trend) => {
    const colors = {
      'improving': 'text-green-600',
      'declining': 'text-red-600',
      'stable': 'text-yellow-600',
      'no-trend': 'text-gray-600'
    };
    return colors[trend];
  };

  const SummaryReport = () => (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{reportStats.totalGoals}</p>
              <p className="text-gray-900">Total Goals</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <User className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{reportStats.totalStudents}</p>
              <p className="text-gray-900">Students</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{reportStats.totalProgressEntries}</p>
              <p className="text-gray-900">Progress Entries</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <PieChart className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{reportStats.averageProgress}%</p>
              <p className="text-gray-900">Avg Progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Goal Status Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(reportStats.statusCounts).map(([status, count]) => (
            <div key={status} className="text-center">
              <div className={`text-2xl font-bold ${getStatusColor(status)}`}>
                {count}
              </div>
              <div className="text-sm text-gray-900">{status}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Domain Distribution */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Goals by Domain</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(reportStats.domainCounts).map(([domain, count]) => (
            <div key={domain} className="flex justify-between items-center px-4 py-3 border border-gray-200 rounded">
              <span className="font-medium text-gray-900">{domain}</span>
              <span className="text-lg font-bold text-primary">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const DetailedReport = () => {
    const studentGroups = filteredData.reduce((acc, goal) => {
      const student = students.find(s => s.id === goal.studentId);
      if (student) {
        const key = student.id;
        if (!acc[key]) {
          acc[key] = { student, goals: [] };
        }
        acc[key].goals.push(goal);
      }
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {Object.values(studentGroups).map(({ student, goals }) => (
          <div key={student.id} className="bg-white p-6 rounded-lg border">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {student.firstName} {student.lastName}
                </h3>
                <p className="text-gray-900">{student.grade} Grade • {goals.length} goals</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-900">IEP End Date</div>
                <div className="font-medium text-gray-900">{formatDate(student.iepEndDate)}</div>
              </div>
            </div>

            <div className="space-y-4">
              {goals.map(goal => {
                const trend = getProgressTrend(goal);
                const latestProgress = goal.progressData[goal.progressData.length - 1];
                
                return (
                  <div key={goal.id} className="border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {goal.domain}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            goal.status === 'Not Started' ? 'bg-gray-100 text-gray-800' :
                            goal.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                            goal.status === 'Mastered' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {goal.status}
                          </span>
                        </div>
                        <p className="text-gray-900 font-medium mb-2">{goal.description}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900">
                          <div>
                            <span className="font-medium">Target:</span> {goal.targetCriteria || 'Not specified'}
                          </div>
                          <div>
                            <span className="font-medium">Review:</span> {goal.reviewFrequency}
                          </div>
                          <div>
                            <span className="font-medium">Progress Entries:</span> {goal.progressData.length}
                          </div>
                          <div>
                            <span className="font-medium">Last Update:</span> {
                              latestProgress ? formatDate(latestProgress.date) : 'Never'
                            }
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        <div className={`text-2xl font-bold ${getTrendColor(trend)}`}>
                          {latestProgress ? `${latestProgress.value}%` : 'No data'}
                        </div>
                        <div className={`text-sm ${getTrendColor(trend)} flex items-center`}>
                          <span className="mr-1">{getTrendIcon(trend)}</span>
                          {trend.replace('-', ' ')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-900 mt-1">
            Generate comprehensive IEP progress reports
          </p>
        </div>
        
        <button
          onClick={handlePrintReport}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary print:hidden"
        >
          <Download size={16} className="mr-2" />
          Print Report
        </button>
      </div>

      {/* Report Filters */}
      <div className="bg-white p-6 rounded-lg border print:hidden">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="student-select" className="block text-sm font-medium text-gray-900 mb-2">
              <Filter size={16} className="inline mr-1" />
              Student
            </label>
            <select
              id="student-select"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Students</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.firstName} {student.lastName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="report-type" className="block text-sm font-medium text-gray-900 mb-2">
              <FileText size={16} className="inline mr-1" />
              Report Type
            </label>
            <select
              id="report-type"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="summary">Summary Report</option>
              <option value="detailed">Detailed Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              <Calendar size={16} className="inline mr-1" />
              Date Range
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Header for Print */}
      <div className="hidden print:block bg-white p-6 border-b">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">IEP Progress Report</h1>
          <p className="text-gray-900 mt-2">
            Generated on {formatDate(new Date())} • 
            Period: {formatDate(new Date(dateRange.start))} to {formatDate(new Date(dateRange.end))}
          </p>
          {selectedStudentId !== 'all' && (
            <p className="text-gray-900">
              Student: {students.find(s => s.id === selectedStudentId)?.firstName} {students.find(s => s.id === selectedStudentId)?.lastName}
            </p>
          )}
        </div>
      </div>

      {/* Report Content */}
      {filteredData.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No data found</h3>
          <p className="text-gray-900 mb-4">
            Try adjusting your filters or date range, or create some goals first.
          </p>
          <button
            onClick={() => onNavigate('goals')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <BarChart3 size={16} className="mr-2" />
            Manage Goals
          </button>
        </div>
      ) : (
        <div className="print:bg-white">
          {reportType === 'summary' ? <SummaryReport /> : <DetailedReport />}
        </div>
      )}

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 1in;
            size: letter;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:block {
            display: block !important;
          }
          
          .print\\:bg-white {
            background-color: white !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            color-adjust: exact;
            background-color: white !important;
            color: black !important;
          }
          
          * {
            background-color: white !important;
            color: black !important;
          }
          
          .text-gray-900 {
            color: black !important;
          }
          
          .text-gray-600, .text-gray-500 {
            color: #4b5563 !important;
          }
          
          .text-primary {
            color: #3b82f6 !important;
          }
          
          .text-green-600 {
            color: #059669 !important;
          }
          
          .text-red-600 {
            color: #dc2626 !important;
          }
          
          .text-yellow-600 {
            color: #d97706 !important;
          }
          
          .bg-white, .bg-gray-50, .bg-gray-100 {
            background-color: white !important;
          }
          
          .bg-blue-100 {
            background-color: #dbeafe !important;
          }
          
          .bg-green-100 {
            background-color: #d1fae5 !important;
          }
          
          .bg-yellow-100 {
            background-color: #fef3c7 !important;
          }
          
          .bg-red-100 {
            background-color: #fee2e2 !important;
          }
          
          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          
          .text-blue-800 {
            color: #1e40af !important;
          }
          
          .text-green-800 {
            color: #065f46 !important;
          }
          
          .text-yellow-800 {
            color: #92400e !important;
          }
          
          .text-red-800 {
            color: #991b1b !important;
          }
          
          .text-gray-800 {
            color: #1f2937 !important;
          }
          
          .border, .border-l-4 {
            border-color: #d1d5db !important;
          }
          
          .border-blue-500 {
            border-color: #3b82f6 !important;
          }
          
          .shadow, .shadow-sm {
            box-shadow: none !important;
          }
        }
      `}</style>

      <ScreenReaderContent>
        Report showing {reportStats.totalGoals} goals for {reportStats.totalStudents} students 
        with {reportStats.totalProgressEntries} progress entries and average progress of {reportStats.averageProgress}%.
      </ScreenReaderContent>
    </div>
  );
};

export default Reports;